"use client";

import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { ChevronDown, ChevronUp, Plus, Trash } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function FAQUniversity({ content, onContentChange, isSelected }: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#054E37";
  const fontFamily = content.fontFamily || "inherit";
  
  // Keep track of open state for each index
  const [openStates, setOpenStates] = useState<Record<number, boolean>>({
    0: true,
    1: true,
  });

  const toggleState = (idx: number) => {
    setOpenStates((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const items = content.items || [
    { q: "What is this theme designed for?", a: "This theme is specifically designed for educational institutions, online courses, schools, universities, and e-learning platforms. If you want to build an Education or E-learning website, Educeet is a perfect theme." },
    { q: "Do you provide theme updates?", a: "This theme is specifically designed for educational institutions, online courses, schools, universities, and e-learning platforms. If you want to build an Education or E-learning website, Educeet is a perfect theme." },
    { q: "Is this theme compatible with Elementor?", a: "Yes, it is fully compatible with Elementor page builder, allowing you to design pages visually." },
    { q: "Is this theme compatible with Elementor?", a: "Yes, it is fully compatible with Elementor page builder, allowing you to design pages visually." },
    { q: "Why do we need to use LMS plugins?", a: "LMS plugins allow you to manage courses, lessons, quizzes, and student enrollments directly in WordPress." },
    { q: "Do you provide theme updates?", a: "Yes, we regularly release updates to ensure compatibility with the latest versions of platform tools." },
    { q: "Do you offer a free trial for Educeet Theme?", a: "We do not offer a direct free trial, but you can explore the live sandbox demo." },
    { q: "Are your services available globally?", a: "Yes, our educational platforms and support services are accessible worldwide." }
  ];

  const updateItem = (index: number, key: string, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [key]: value };
    onContentChange?.({ ...content, items: newItems });
  };

  const addItem = () => {
    const newItems = [...items, { q: "New Question?", a: "New Answer text." }];
    onContentChange?.({ ...content, items: newItems });
  };

  const removeItem = (idx: number) => {
    const newItems = items.filter((_: any, i: number) => i !== idx);
    onContentChange?.({ ...content, items: newItems });
  };

  const renderCard = (item: any, originalIndex: number) => {
    const isOpen = !!openStates[originalIndex];
    return (
      <div
        key={originalIndex}
        className="relative bg-[#F4F6F8] rounded-xl border border-gray-100/50 p-6 transition-all duration-300"
      >
        {/* Canvas Level Delete Button */}
        {isSelected && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeItem(originalIndex);
            }}
            className="absolute -top-2 -right-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 shadow-md transition-colors"
          >
            <Trash className="h-3 w-3" />
          </button>
        )}

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 text-left">
            <h4 className="text-lg font-bold text-[#1E293B] leading-snug">
              <EditableText
                value={item.q}
                onChange={(val) => updateItem(originalIndex, "q", val)}
                as="span"
                placeholder="Question"
              />
            </h4>
          </div>
          <button
            onClick={() => toggleState(originalIndex)}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0 transition-transform duration-300"
            style={{ backgroundColor: primaryColor }}
          >
            {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1, marginTop: 12 }}
              exit={{ height: 0, opacity: 0, marginTop: 0 }}
              className="overflow-hidden"
            >
              <p className="text-sm text-[#5C6B73] leading-relaxed text-left border-t border-gray-200/40 pt-3">
                <EditableText
                  value={item.a}
                  onChange={(val) => updateItem(originalIndex, "a", val)}
                  as="span"
                  placeholder="Answer..."
                />
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <section className={`py-24 px-6 lg:px-8 bg-white ${isSelected ? "ring-2 ring-blue-500" : ""}`} style={{ fontFamily }}>
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-[#1A1A1A] tracking-tight mb-4">
            <EditableText
              value={content.title || "Frequently Asked Questions"}
              onChange={(v) => onContentChange?.({ ...content, title: v })}
              as="span"
              placeholder="FAQ Title"
            />
          </h2>
          {content.subtitle !== "" && (
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              <EditableText
                value={content.subtitle || "Have questions? We're here to help."}
                onChange={(v) => onContentChange?.({ ...content, subtitle: v })}
                as="span"
                placeholder="FAQ Subtitle"
              />
            </p>
          )}
        </div>

        {/* 2-Column Grid Accordions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* Left Column */}
          <div className="flex flex-col gap-6">
            {items.map((item: any, idx: number) => {
              if (idx % 2 === 0) return renderCard(item, idx);
              return null;
            })}
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-6">
            {items.map((item: any, idx: number) => {
              if (idx % 2 !== 0) return renderCard(item, idx);
              return null;
            })}
          </div>
        </div>

        {/* Canvas Level Add Button */}
        {isSelected && (
          <div className="text-center mt-12">
            <button
              onClick={addItem}
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-gray-500 hover:bg-gray-100 hover:text-gray-600 transition-all rounded-xl font-semibold"
            >
              <Plus className="w-5 h-5" />
              <span>Add FAQ Item</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
